import React, { useMemo } from "react";
import { useRates } from "@/lib/hooks/use-rates";

import { Token } from "@/lib/classes/BaseNetwork";
import { useWidget } from "@/lib/hooks/use-widget";
import BalanceRow from "@/lib/profile/balance-row";
import { useTokenMarketData } from "@/lib/hooks/use-token-marketdata";
import { useTotalBalance } from "@/lib/hooks/use-total-balance";
import { Skeleton } from "@/lib/components/ui/skeleton";
import Unfunded from "@/lib/profile/unfunded";

interface TokenDataWithImages extends Token {
  image: string;
  platformImages: Record<string, string>;
}

const UserWallet = () => {
  const { isLoading: isBalancesLoading } = useWidget();
  const { rates, isLoading: isRatesLoading, marketData } = useRates();
  const totalBalance = useTotalBalance();
  const tokensArray = useTokenMarketData();

  const allTokens = useMemo(() => {
    if (isBalancesLoading || isRatesLoading) return [];

    return tokensArray.map(token => {
      const value =
        Number(token.balanceFormatted) * rates[token.symbol.toUpperCase()];

      return {
        ...token,
        value: isNaN(value) ? 0 : value,
      };
    });
  }, [isBalancesLoading, isRatesLoading, tokensArray, rates]);

  const mergedArray = useMemo(() => {
    return [...allTokens]
      .reduce((prev, token) => {
        const otherToken = prev.find(t => t.symbol === token.symbol);

        const newToken = token;

        if (otherToken) {
          const newPrev = prev.filter(
            t => t.tokenAddress !== otherToken.tokenAddress,
          );

          const uniquePlatforms = [
            ...(otherToken.platforms ?? [otherToken.chain]),
            newToken.chain,
          ];

          const platformImages = uniquePlatforms.reduce<Record<string, string>>(
            (acc, chain) => {
              const chainIcon = allTokens.find(t => t.symbol === chain)?.image;
              if (chainIcon) acc[chain] = chainIcon;
              return acc;
            },
            {},
          );

          return [
            ...newPrev,
            {
              ...newToken,
              balance: (
                BigInt(token.balance) + BigInt(otherToken.balance)
              ).toString(),
              balanceFormatted: (
                parseFloat(token.balanceFormatted) +
                parseFloat(otherToken.balanceFormatted)
              ).toString(),
              platforms: uniquePlatforms,
              platformImages,
            },
          ];
        }

        const tokenChain = allTokens.find(t => t.symbol === token.chain);

        return [
          ...prev,
          {
            ...token,
            platforms: [token.chain],
            platformImages: tokenChain
              ? { [tokenChain.symbol]: tokenChain.image }
              : {},
          },
        ];
      }, [] as TokenDataWithImages[])
      .map(t => {
        const mData = marketData.find(
          m =>
            m.symbol.toLowerCase() === t.symbol.toLowerCase() ||
            (m.symbol === "pol" && t.symbol === "MATIC"),
        );

        return {
          ...t,
          name: mData?.name || t.name,
          price: mData?.current_price || 0,
          change: mData?.price_change_percentage_24h || 0,
          value: mData?.current_price
            ? mData.current_price * parseFloat(t.balanceFormatted)
            : 0,
          cap: mData?.market_cap || 0,
        };
      })
      .sort((a, b) => b.value - a.value)
      .filter(s => parseFloat(s.balanceFormatted) > 0 || s.value > 0);
  }, [allTokens, marketData]);

  if (isBalancesLoading || isRatesLoading) {
    return <Skeleton className="h-full w-full min-h-32" />;
  }

  if (totalBalance === 0) return <Unfunded />;

  return (
    <>
      {mergedArray.map(token => (
        <BalanceRow key={token.tokenAddress} token={token} />
      ))}
    </>
  );
};

export default UserWallet;
